import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../../components/Learner/Layout';
import QuickPracticeComponent from '../../../../components/Learner/QuickPractice';
import { getDrillVocabQuestions, submitDrillVocab } from '../../../../services/practiceApi';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import DrillVocabLoadingOverlay from '../../../../components/Learner/Practice/DrillVocabLoadingOverlay';
import { usePracticeSession, isPageReload } from '../../../../hooks/usePracticeSession';

const DrillVocab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subcourseId = location.state?.subcourseId;
  const fromPage = location.state?.fromPage || '/learner/language-learning';

  const { saveSession, loadSession, clearSession } = usePracticeSession('vocab-drill');

  const clearVocabStorage = () => {
    clearSession();
    try { localStorage.removeItem(`qp-answers-${sessionData?.['session-id']}`); } catch (_) {}
  };

  useEffect(() => {
    const fetchOrRestore = async () => {
      setLoading(true);
      setError(null);

      // Restore from localStorage on page reload, or when subcourseId is not available
      if (isPageReload() || !subcourseId) {
        const saved = loadSession();
        if (saved?.sessionData && saved?.questions?.length > 0) {
          setSessionData(saved.sessionData);
          setQuestions(saved.questions);
          setLoading(false);
          return;
        }
        if (!subcourseId) {
          setError('Missing subcourse-id. Please go back and try again.');
          setLoading(false);
          return;
        }
      }

      const result = await getDrillVocabQuestions({ subcourseId });

      if (result.success && result.data) {
        setSessionData(result.data);
        const problems = result.data.problems || [];
        setQuestions(problems);
        saveSession(result.data, problems, location.state);
      } else {
        setError(result.message || 'Failed to load vocab drill questions');
      }

      setLoading(false);
    };

    fetchOrRestore();
  }, [subcourseId]);

  const handlePracticeComplete = async (answers) => {
    if (!sessionData) {
      alert('Session data is missing. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const userAnswers = questions.map((question, questionIndex) => {
        const drillId = question['drill-id'];
        const questionType = question.type || '';

        let existingAnswer = answers.find(a => {
          const aId = a.questionId || a['soal-id'] || a['question-id'];
          return aId === drillId || String(aId) === String(drillId);
        });
        if (!existingAnswer && answers[questionIndex]) {
          existingAnswer = answers[questionIndex];
        }

        let answer = existingAnswer?.answer;

        if (questionType === 'pilgan-single') {
          if (answer === undefined || answer === null || answer === '') {
            answer = null;
          } else {
            const num = typeof answer === 'number' ? answer : parseInt(answer, 10);
            answer = isNaN(num) ? null : num;
          }
        } else if (questionType === 'pilgan-multi') {
          if (!Array.isArray(answer)) {
            answer = answer !== undefined && answer !== null ? [answer] : [];
          }
          answer = answer.map(v => {
            const num = typeof v === 'number' ? v : parseInt(v, 10);
            return isNaN(num) ? null : num;
          }).filter(v => v !== null);
        } else if (questionType === 'pairing') {
          if (Array.isArray(answer)) {
            const left = [];
            const right = [];
            answer.forEach(pair => {
              if (pair && typeof pair === 'object') {
                if (pair.left !== undefined) left.push(pair.left);
                if (pair.right !== undefined) right.push(pair.right);
              }
            });
            answer = { left, right };
          } else if (typeof answer !== 'object' || answer === null) {
            answer = { left: [], right: [] };
          }
        }

        return {
          'drill-id': drillId,
          answer,
          type: questionType,
          duration: existingAnswer?.duration || 0,
        };
      });

      const result = await submitDrillVocab({
        sessionId: sessionData['session-id'],
        userAnswers,
      });

      setIsSubmitting(false);

      const apiData = result.data || {};
      // changed by Cursor: level/old-level/delta-progress sekarang ada di to-display (bukan learner-subcourse-stats)
      const toDisplay = apiData['to-display'] || {};

      let language = sessionData?.['course-name'] || null;
      if (!language) {
        if (fromPage?.includes('english')) {
          language = 'English language learning';
        } else if (fromPage?.includes('japanese')) {
          language = 'Japanese language learning';
        } else {
          language = 'German language learning';
        }
      }

      const score = toDisplay['session-accuracy'] ?? 0;
      const soalsStats = toDisplay['soals-stats'] || [];
      const correctCount = soalsStats.filter(s => s.accuracy === 100).length;
      const currentLevel = toDisplay['level'] ?? 0;
      const previousLevel = toDisplay['old-level'] ?? currentLevel;
      const deltaLevel = toDisplay['delta-progress'] ?? 0;

      const results = {
        language,
        courseType: 'Drill Vocab',
        level: sessionData?.['subcourse-name'] || 'All Levels',
        score,
        correctCount,
        totalQuestions: questions.length,
        currentLevel,
        previousLevel,
        deltaLevel,
        practiceParams: { subcourseId },
        apiResponse: apiData,
        originalQuestions: questions,
        userAnswers: answers,
        sessionData,
        fromPage,
      };

      // changed by Cursor: use ResultPage (same as subcourse practice)
      clearVocabStorage();
      navigate('/learner/result', { state: results });
    } catch (err) {
      console.error('❌ Error submitting drill vocab:', err);
      setIsSubmitting(false);

      const correctCount = answers.filter(a => a.isCorrect).length;
      const score = Math.round((correctCount / questions.length) * 100);

      let language = sessionData?.['course-name'] || null;
      if (!language) {
        if (fromPage?.includes('english')) {
          language = 'English language learning';
        } else if (fromPage?.includes('japanese')) {
          language = 'Japanese language learning';
        } else {
          language = 'German language learning';
        }
      }

      clearVocabStorage();
      navigate('/learner/result', {
        state: {
          language,
          courseType: 'Drill Vocab',
          level: sessionData?.['subcourse-name'] || 'All Levels',
          score,
          correctCount,
          totalQuestions: questions.length,
          currentLevel: 0,
          previousLevel: 0,
          deltaLevel: 0,
          practiceParams: { subcourseId },
          apiResponse: null,
          originalQuestions: questions,
          userAnswers: answers,
          sessionData,
          fromPage,
        },
      });
    }
  };

  const handleClose = () => {
    clearVocabStorage();

    if (fromPage) {
      navigate(fromPage);
      return;
    }
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      navigate(lastOpenedUrl);
      return;
    }
    navigate('/learner/language-learning');
  };

  if (loading) {
    return (
      <DrillVocabLoadingOverlay
        visible={true}
        text="Loading vocab drill questions..."
        subtext="Please wait a moment"
      />
    );
  }

  if (error) {
    return (
      <Layout hideSidebar>
        <motion.div
          className="w-full flex flex-col items-center justify-center min-h-screen p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          <div className="py-10 text-center">
            <p className="text-[#d32f2f] mb-5">Error: {error}</p>
            <motion.button
              type="button"
              data-track-action="drill-vocab-error-back"
              onClick={handleClose}
              className="py-2.5 px-5 bg-[#1d4746] text-white border-none rounded-lg cursor-pointer font-semibold hover:bg-[#12766d] transition-colors duration-200"
              whileTap={{ scale: 0.96 }}
            >
              Back
            </motion.button>
          </div>
        </motion.div>
      </Layout>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Layout hideSidebar>
        <motion.div
          className="w-full flex flex-col items-center justify-center min-h-screen p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          <div className="py-10 text-center">
            <p className="text-[var(--text-primary)] mb-5">No vocab drill questions available.</p>
            <motion.button
              type="button"
              data-track-action="drill-vocab-empty-back"
              onClick={handleClose}
              className="py-2.5 px-5 bg-[#1d4746] text-white border-none rounded-lg cursor-pointer font-semibold hover:bg-[#12766d] transition-colors duration-200 mt-5"
              whileTap={{ scale: 0.96 }}
            >
              Back
            </motion.button>
          </div>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout hideSidebar>
      <div className="w-full p-6 md:p-4 md:px-6 md:flex md:flex-col md:items-center md:justify-center md:min-h-screen max-md:p-0 max-md:pb-[100px]">
        <DrillVocabLoadingOverlay
          visible={isSubmitting}
          text="Submitting your answers..."
          subtext="Please wait a moment"
        />
        <QuickPracticeComponent
          questions={questions}
          onComplete={handlePracticeComplete}
          onClose={handleClose}
          hideCompletedScreen={true}
          sessionData={sessionData}
          practiceTitle="Drill Vocab - Quick Practice"
          variant="drill"
        />
      </div>
    </Layout>
  );
};

export default DrillVocab;
